import {
  Button,
  IconButton,
  Link,
  Loader,
  Offscreen,
  Panel,
  PanelContent,
  PanelHeader,
  TextField
} from '@deque/cauldron-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthContext } from '~/common/contexts/auth';
import { useEnterprises } from '~/common/contexts/enterprises';
import useV2EnterpriseMembers from '~/common/hooks/useV2EnterpriseMembers';

import { type Team } from '~/common/api-client';
import { MAX_TEAM_NAME_LENGTH } from '~/common/constants';
import { EditVariant } from '../../types';
import { deduplicateUserIds, getCurrentUserIds } from '../../utils/teams-utils';
import { AddUsers, getComboboxLabel, isAcceptedMember } from './AddUsers';
import styles from './SettingSteps.css';

export const TEAM_MEMBERS_PREVIOUS_ID = 'team-members-previous';

export interface SettingStepsProps {
  variant: EditVariant;
  team: Team;
  setTeam: (team: Team) => void;
  nextStep: () => void;
  prevStep: () => void;
  gotoStep: (step: number) => void;
}

export const BasicData: React.FC<SettingStepsProps> = ({
  team,
  setTeam,
  nextStep
}) => {
  const { t } = useTranslation();

  const [nameError, setNameError] = useState<string>();

  const nameFieldRef = useRef<HTMLInputElement | null>(null);

  const validateTeamName = (value?: string) => {
    if (!value || !value?.length) {
      return t('Cannot be empty or blank');
    }
    if (value?.length > MAX_TEAM_NAME_LENGTH) {
      return t(`Cannot be longer than {{maxLength}} characters`, {
        maxLength: MAX_TEAM_NAME_LENGTH
      });
    }
    return undefined;
  };

  const onNext = () => {
    const name = nameFieldRef.current?.value.trim();
    const newNameError = validateTeamName(name);
    if (!newNameError) {
      // The name has been validated at this point, and is not undefined
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setTeam({ ...team, name: name! });
      nextStep();
    } else {
      setNameError(newNameError);
      nameFieldRef.current?.focus();
    }
  };

  return (
    <Panel className={styles.settingsPanel}>
      <PanelHeader className={styles.panelHeader}>
        <h2>{t('Provide Team Name')}</h2>
        <Button disabled variant="link">
          {t('Skip')}
        </Button>
      </PanelHeader>
      <PanelContent>
        <div className={styles.innerPanel}>
          <TextField
            name="teamName"
            fieldRef={nameFieldRef}
            label={t('Team Name')}
            defaultValue={team.name}
            error={nameError}
            required
            autoFocus
          />
        </div>
      </PanelContent>
      <PanelContent className={styles.panelFooter}>
        <div>
          <Button type="button" onClick={onNext}>
            {t('Next')}
          </Button>
        </div>
        <Link href="/teams">{t('Cancel and exit wizard')}</Link>
      </PanelContent>
    </Panel>
  );
};

export const TeamMembers: React.FC<SettingStepsProps> = ({
  team,
  setTeam,
  nextStep,
  prevStep
}) => {
  const skipButtonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();

  const [usersToAdd, setUsersToAdd] = useState<string[]>([]);
  const [usersToRemove, setUsersToRemove] = useState<string[]>([]);

  useEffect(() => {
    if (skipButtonRef.current) {
      skipButtonRef.current.focus();
    }
  }, [skipButtonRef.current]);

  const onNext = () => {
    const [user_ids_to_add, user_ids_to_remove] = deduplicateUserIds(
      team,
      usersToAdd,
      usersToRemove
    );
    setTeam({
      ...team,
      user_ids_to_add,
      user_ids_to_remove
    });
    nextStep();
  };

  return (
    <Panel className={styles.settingsPanel}>
      <PanelHeader className={styles.panelHeader}>
        <h2>{t(`Add Members to "{{name}}"`, { name: team.name })}</h2>
        <Button variant="link" onClick={nextStep} ref={skipButtonRef}>
          {t('Skip member changes')}
        </Button>
      </PanelHeader>
      <PanelContent>
        <div className={styles.innerPanel}>
          <AddUsers
            userIds={getCurrentUserIds(team)}
            setUsersToAdd={setUsersToAdd}
            setUsersToRemove={setUsersToRemove}
          />
        </div>
      </PanelContent>
      <PanelContent className={styles.panelFooter}>
        <div>
          <Button
            id={TEAM_MEMBERS_PREVIOUS_ID}
            onClick={prevStep}
            variant="secondary"
          >
            {t('Previous')}
          </Button>
          <Button type="button" onClick={onNext}>
            {t('Next')}
          </Button>
        </div>
        <Link href="/teams">{t('Cancel and exit wizard')}</Link>
      </PanelContent>
    </Panel>
  );
};

export const Review: React.FC<SettingStepsProps> = ({
  team,
  variant,
  prevStep,
  gotoStep
}) => {
  const { t } = useTranslation();

  const { user } = useAuthContext();
  const { activeEnterprise } = useEnterprises();

  const token = user?.token;
  const enterpriseId = activeEnterprise?.id;

  const { pendingOrAcceptedMembers, loading: loadingMembers } =
    useV2EnterpriseMembers({ token, enterpriseId });

  if (loadingMembers) {
    return <Loader label={t('Loading users...')} tabIndex={-1} />;
  }

  const rowForUserId = (userId: string) => {
    const foundUser = pendingOrAcceptedMembers?.find(enterpriseMember =>
      isAcceptedMember(enterpriseMember)
        ? enterpriseMember.user_id === userId
        : enterpriseMember.email === userId
    );

    return (
      foundUser && (
        <li key={`user-${foundUser.email}`}>{getComboboxLabel(foundUser)}</li>
      )
    );
  };

  const submitLabel = () => {
    switch (variant) {
      case EditVariant.edit:
        return t('Update Team');
      case EditVariant.create:
        return t('Create Team');
    }
  };

  return (
    <Panel className={styles.settingsPanel}>
      <PanelHeader className={styles.panelHeader}>
        <h2>{t(`Review "{{name}}"`, { name: team.name })}</h2>
      </PanelHeader>
      <PanelContent>
        <div className={styles.innerPanel}>
          <div className={styles.reviewHeader}>
            <h3>{t('Team Details')} </h3>
            <IconButton
              icon="pencil"
              label={
                <>
                  {t('Edit')} <Offscreen> {t('Team Details')}</Offscreen>
                </>
              }
              onClick={() => gotoStep(0)}
            />
          </div>
          <dl>
            <dt id="team-name-label">{t('Team Name')}</dt>
            <dd aria-labelledby="team-name-label">{team.name}</dd>
          </dl>
        </div>
      </PanelContent>
      <PanelContent>
        <div className={styles.innerPanel}>
          <div className={styles.reviewHeader}>
            <h3>{t('Team Members')} </h3>
            <IconButton
              icon="pencil"
              label={
                <>
                  {t('Edit')} <Offscreen> {t('Team Members')}</Offscreen>
                </>
              }
              onClick={() => gotoStep(1)}
            />
          </div>
          <dl>
            <dt id="team-members-added-label">
              {t(`Added ({{addedCount}})`, {
                addedCount: team.user_ids_to_add.length
              })}
            </dt>
            <dd aria-labelledby="team-members-added-label">
              {team.user_ids_to_add.length ? (
                <ul>
                  {team.user_ids_to_add.map(user_id => rowForUserId(user_id))}
                </ul>
              ) : (
                <>{t('No users added')}</>
              )}
            </dd>
            {variant === EditVariant.edit && (
              <>
                <dt id="team-members-removed-label">
                  {t(`Removed ({{removedCount}})`, {
                    removedCount: team.user_ids_to_remove.length
                  })}
                </dt>
                <dd aria-labelledby="team-members-removed-label">
                  {team.user_ids_to_remove.length ? (
                    <ul>
                      {team.user_ids_to_remove.map(user_id =>
                        rowForUserId(user_id)
                      )}
                    </ul>
                  ) : (
                    <>{t('No users removed')}</>
                  )}
                </dd>
              </>
            )}
          </dl>
        </div>
      </PanelContent>
      <PanelContent className={styles.panelFooter}>
        <div>
          <Button onClick={prevStep} variant="secondary">
            {t('Previous')}
          </Button>
          <Button type="submit">{submitLabel()}</Button>
        </div>
        <Link href="/teams">{t('Cancel and exit wizard')}</Link>
      </PanelContent>
    </Panel>
  );
};
